/**
 * chippie-bridge.js — the single window.ChippieBridge facade (transport SSOT).
 *
 * Chippie runs the same web app inside three runtimes, each with a different way
 * to reach the device:
 *
 *   - iOS Hotwire Native shell  -> Strada bridge components (this.send over the
 *                                  @hotwired/hotwire-native-bridge channel)
 *   - Android Hotwire Native shell -> a hand-rolled @JavascriptInterface named
 *                                  window.ChippieNative (NOT Hotwire's bridge)
 *   - plain PWA / desktop browser -> Web APIs (navigator.vibrate/share/geo/...)
 *
 * Rather than make every caller branch on the runtime, callers use a single flat
 * API — window.ChippieBridge.haptic(), .share(...), .location(), etc. — and this
 * facade owns the per-call transport selection. It is loaded as a plain head
 * script (like turbo-utils.js / skeleton-nav.js), BEFORE controllers.bundle, so
 * window.ChippieBridge exists by the time the bridge controllers' module code
 * runs and calls register().
 *
 * SSOT split (do not blur it):
 *   - This file owns ONLY runtime detection + dispatch. It has zero per-component
 *     behaviour. Adding a 26th component never edits this file.
 *   - Each component's behaviour lives in its bridge controller
 *     (static/js/controllers/esm/bridge/<name>_controller.js). On connect (and at
 *     module load for the standalone Android/PWA fallback) the controller calls
 *     ChippieBridge.register("<name>", { ios, android, pwa }) to contribute its
 *     three implementations. The facade then exposes ChippieBridge.<name>(...).
 *   - Async native results (a scanned barcode, an FCM token, a permission grant)
 *     do NOT flow back through here. Each controller installs its own
 *     window.*Resolve global that the native Kotlin/Swift evaluates into; the
 *     facade stays transport-only.
 *
 * Idempotent: guarded so a Turbo navigation re-running the head script (or a
 * double include) never clobbers an already-built facade or its registrations.
 */
(function () {
  "use strict";

  if (window.ChippieBridge) return; // already installed — keep registrations

  /**
   * Resolve the current runtime once per call (cheap; never cached so it stays
   * correct if the shell injects ChippieNative late).
   *   - "android": the Android shell's @JavascriptInterface is present. Checked
   *     FIRST because the Android UA also carries the "Hotwire Native" token.
   *   - "ios": Hotwire Native UA token but no ChippieNative -> the iOS shell.
   *   - "pwa": neither -> plain browser (mobile or desktop).
   */
  function platform() {
    if (window.ChippieNative) return "android";
    if (/Hotwire Native/.test(navigator.userAgent || "")) return "ios";
    return "pwa";
  }

  window.ChippieBridge = {
    _platform: platform,

    /**
     * Register a component's three implementations and expose it on the facade.
     *
     * impls is { ios, android, pwa } where each value is EITHER:
     *   - a function  -> ChippieBridge.<name>(...args) calls the platform's fn
     *   - an object    -> a namespace (e.g. localNotification.{schedule,cancel});
     *                     ChippieBridge.<name>.<method>(...args) dispatches per
     *                     platform at call time.
     *
     * Dispatch resolves the platform on every call (not at register time) so the
     * same registration is correct even if a controller registers a placeholder
     * at module load and the real iOS send on connect(). A missing impl for the
     * resolved platform falls back to the pwa impl; a missing pwa impl no-ops
     * (returns undefined) rather than throwing — a bridge gap must never break a
     * caller.
     */
    register: function (name, impls) {
      var sample = impls.pwa || impls.ios || impls.android;

      if (typeof sample === "function") {
        this[name] = function () {
          var impl = impls[platform()] || impls.pwa;
          return typeof impl === "function"
            ? impl.apply(null, arguments)
            : undefined;
        };
        return;
      }

      if (sample && typeof sample === "object") {
        // Namespace object: build one per-method dispatcher per key in the sample.
        var ns = {};
        Object.keys(sample).forEach(function (method) {
          ns[method] = function () {
            var impl = impls[platform()] || impls.pwa;
            var fn = impl && impl[method];
            return typeof fn === "function" ? fn.apply(null, arguments) : undefined;
          };
        });
        this[name] = ns;
      }
    },
  };
})();
